import { useState } from "react";

import { brandLogo } from "constants/brandLogos";

/**
 * The square brand tile: the brand's hosted logo where one exists (36 of 313 —
 * `constants/brandLogos.js`, keyed by customlist brand id), the brand's first letter
 * otherwise. A logo that fails to load falls back to the letter too.
 *
 * One component because two surfaces render it — `BrandsDropdown`'s rows (`sm`, 34px)
 * and `/brands`' cards (`lg`, 54px, the reference's brand-card tile size) — and the
 * logo-or-letter-or-error logic must not fork.
 *
 * Logos sit on a white tile with a hairline border (they are colour-on-white marks, the
 * `OurPartners` treatment); letter tiles keep the reference's `bg-soft`/blue.
 *
 * A raw `<img>`, deliberately, despite rule 2: the shared `<Image>`'s `onError` swaps in
 * a generic placeholder *image*, and the right fallback here isn't an image at all — it
 * is the letter tile. `alt=""` because the brand name is always adjacent text;
 * `loading="lazy"` because hundreds of tiles mount at once and only the scrolled-to
 * logos need fetching.
 */
const SIZES = {
  sm: { box: "h-[34px] w-[34px]", pad: "p-[3px]", letter: "text-sm" },
  lg: { box: "h-[54px] w-[54px]", pad: "p-[6px]", letter: "text-lg" },
};

const brandInitial = (name) => {
  const match = String(name || "").match(/[a-z0-9]/i);
  return match ? match[0].toUpperCase() : "#";
};

export default function BrandTile({ id, name, size = "sm" }) {
  const [failed, setFailed] = useState(false);
  const { box, pad, letter } = SIZES[size] || SIZES.sm;
  const logo = brandLogo(id);

  if (!logo || failed) {
    return (
      <span
        className={`inline-flex ${box} shrink-0 items-center justify-center rounded-md bg-soft font-sans ${letter} font-bold text-blue-600`}
      >
        {brandInitial(name)}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex ${box} shrink-0 items-center justify-center overflow-hidden rounded-md border border-line-subtle bg-white ${pad}`}
    >
      <img
        src={logo}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className="max-h-full max-w-full object-contain"
      />
    </span>
  );
}
